import { InputSwitch } from "primereact/inputswitch";
import { useEffect, useState } from "react";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import localeText from "../../../../locale";
import ConfirmActionDialog from "../../components/list/ConfirmActionDialog";
import CopyValue from "../../components/list/CopyValue";
import StatusBadge from "../../components/list/StatusBadge";
import { redirectIfUnauthorized } from "../../utils/adminUtils";
import { percentText } from "../../../Channel/channelUtils";
import { callChannel, channelService, showcaseUrl, showcaseUtmUrl } from "./adminScontiPosteUtils";

// Link da vitrine, abrir/fechar o canal e percentual de cashback
const ChannelConfigSection = () => {
  const { showErrorToast, showSuccessToast } = useToastContext();
  const [config, setConfig] = useState(null);
  const [active, setActive] = useState(false);
  const [percent, setPercent] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const apply = (result) => {
    setConfig(result);
    setActive(!!result.active);
    setPercent(String(Number(result.cashbackPercent || 0)));
  };

  useEffect(() => {
    callChannel(() => channelService.adminConfig())
      .then(apply)
      .catch((e) => {
        if (!redirectIfUnauthorized(e)) showErrorToast({ detail: e.message });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!config) {
    return (
      <div className="admin-scp__panel" aria-busy="true">
        <i className="pi pi-spin pi-spinner" aria-hidden="true" />
      </div>
    );
  }

  const currentPercent = Number(config.cashbackPercent || 0);
  const newPercent = Number(String(percent).replace(",", "."));
  const percentChanged = percent !== "" && !Number.isNaN(newPercent) && newPercent !== currentPercent;
  const dirty = percentChanged || active !== !!config.active;

  const save = async () => {
    setConfirmVisible(false);
    setSaving(true);
    try {
      const result = await callChannel(() =>
        channelService.adminSaveConfig({
          active,
          cashbackPercent: percentChanged ? newPercent : undefined,
        }),
      );
      apply(result);
      showSuccessToast({ detail: "adminChannelSaved" });
    } catch (e) {
      if (!redirectIfUnauthorized(e)) showErrorToast({ detail: e.message });
    } finally {
      setSaving(false);
    }
  };

  // Mudar o % com Managers ativos tira os produtos deles da vitrine ate aceitarem: pede confirmacao
  const onSave = () => (percentChanged && config.activeSellers > 0 ? setConfirmVisible(true) : save());

  return (
    <div className="admin-scp__panel">
      <div className="admin-scp__row">
        <div>
          <div className="admin-scp__label">{localeText("adminChannelUrl")}</div>
          <p className="admin-scp__help">{localeText("adminChannelUrlHelp")}</p>
        </div>
        <div className="admin-scp__field">
          <CopyValue value={showcaseUrl()} />
          <CopyValue value={showcaseUtmUrl()} label={localeText("adminChannelUtmUrl")} />
          <a href={showcaseUrl()} target="_blank" rel="noreferrer" className="admin-link-btn">
            <i className="pi pi-external-link" aria-hidden="true" /> {localeText("adminChannelOpen")}
          </a>
        </div>
      </div>

      <div className="admin-scp__row">
        <div>
          <div className="admin-scp__label">{localeText("adminChannelStatus")}</div>
        </div>
        <div className="admin-scp__field">
          <div className="admin-scp__inline">
            <InputSwitch inputId="admin-scp-active" checked={active} onChange={(e) => setActive(e.value)} />
            <label htmlFor="admin-scp-active">
              {active ? localeText("adminChannelActive") : localeText("adminChannelInactive")}
            </label>
            <StatusBadge tone={config.active ? "good" : "neutral"}>
              {config.active ? localeText("adminChannelActive") : localeText("adminChannelInactive")}
            </StatusBadge>
          </div>
          <span className="admin-muted">{localeText("adminChannelActiveSellers", { count: config.activeSellers })}</span>
          {config.sellersWithOutdatedTerms > 0 && (
            <p className="admin-scp__notice admin-scp__notice--warning">
              {localeText("adminChannelOutdated", { count: config.sellersWithOutdatedTerms })}
            </p>
          )}
        </div>
      </div>

      <div className="admin-scp__row">
        <div>
          <label className="admin-scp__label" htmlFor="admin-scp-percent">
            {localeText("adminChannelPercent")}
          </label>
          <p className="admin-scp__help">{localeText("adminChannelPercentHelp")}</p>
        </div>
        <div className="admin-scp__field">
          <input
            id="admin-scp-percent"
            className="admin-scp__input admin-scp__input--percent"
            type="number"
            min="0"
            max="100"
            step="0.01"
            inputMode="decimal"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
          />
        </div>
      </div>

      <div>
        <button type="button" className="admin-btn admin-btn--primary" onClick={onSave} disabled={!dirty || saving}>
          {saving && <i className="pi pi-spin pi-spinner" aria-hidden="true" />}
          {localeText("adminChannelSave")}
        </button>
      </div>

      <ConfirmActionDialog
        visible={confirmVisible}
        title={localeText("adminChannelConfirmPercentTitle")}
        message={localeText("adminChannelConfirmPercent", {
          from: percentText(currentPercent),
          to: percentText(newPercent),
          count: config.activeSellers,
        })}
        confirmLabel={localeText("adminChannelConfirmPercentAction", { to: percentText(newPercent) })}
        loading={saving}
        onConfirm={save}
        onCancel={() => setConfirmVisible(false)}
      />
    </div>
  );
};

export default ChannelConfigSection;
